import { useState, useEffect } from 'react'
import { Search, Lock, Eye, EyeOff, ChevronDown, ChevronUp, Users, Target, MapPin, Award, UserCheck, Megaphone, Home, PoundSterling, GraduationCap, Clock, Settings, Brain, Shield, ClipboardCheck } from 'lucide-react'
import SEOHead from '../components/SEOHead'
import c9LogoImage from '../assets/images/C9LogoDarkTheme.png'
import lcrcaLogoImage from '../assets/images/LCRCA-Logo.png'

export default function LCRCAProposal() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedSections, setExpandedSections] = useState({})
  const [searchResults, setSearchResults] = useState(0)
  const [selectedCategory, setSelectedCategory] = useState('all')

  // Password for access (in production, this should be more secure)
  const correctPassword = 'C9LCRCA2026'

  const handlePasswordSubmit = (e) => {
    e.preventDefault()
    if (password === correctPassword) {
      setIsAuthenticated(true)
      localStorage.setItem('lcrca-auth', 'true')
    } else {
      alert('Incorrect password. Please contact C9 Recruitment for access.')
    }
  }

  useEffect(() => {
    const saved = localStorage.getItem('lcrca-auth')
    if (saved === 'true') {
      setIsAuthenticated(true)
    }
  }, [])

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }

  const sections = [
    {
      id: 'objective',
      title: 'Objective',
      icon: Target,
      content: {
        overview: 'To ensure the timely mobilisation of a fully trained, compliant, and route-ready driver workforce across one or both depots for the Liverpool City Region franchising commencing September 2026.',
        details: [
          'Meets all regulatory and operational requirements (PCV, CPC, Medical & Training)',
          'Is flexible and scalable to adapt to award outcomes', 
          'Is supported by contingency plans to mitigate risks to continuity',
          'Is ready to maintain high service standards from day one'
        ],
        summary: 'The overarching aim is to provide a resilient, low-risk mobilisation solution that supports a smooth transition under the franchising model.'
      }
    },
    {
      id: 'position',
      title: 'Strategic Position & Risks',
      icon: MapPin,
      content: {
        overview: 'The first tranche of the Liverpool City Region Combined Authority (LCRCA) franchising rollout will consist of two operational lots: Wirral and St Helens.',
        risks: [
          'Arriva may only be eligible to win a maximum of one lot under the new franchising model',
          'Disruption to services, missing buses, increased waiting times due to lack of staff',
          'Poor media attention and public perception issues',
          'Arriva may be reluctant to share driver data or provide TUPE transfer confirmation',
          'Potential recruitment freezes in lead-up to franchise handover',
          'Drivers can decide up to 24 hours before actual transfer date'
        ],
        mitigation: 'This creates potential resourcing and onboarding gaps for incoming operators, particularly where driver continuity and route familiarity will reduce training and onboarding requirements.'
      }
    },
    {
      id: 'mobilisation',
      title: 'Driver Mobilisation Overview',
      icon: Users,
      content: {
        requirements: [
          { label: 'Estimated Drivers Required', value: '300+ drivers (evidence-based number subject to PVR data)' },
          { label: 'Franchising Start Date', value: 'September 2026' },
          { label: 'Number of Depots', value: '2 – Wirral & St Helens' },
          { label: 'Working Patterns', value: 'Full-time, part-time, weekend-only' },
          { label: 'Compliance Requirements', value: 'Valid PCV Licence, CPC, Medical & Training, Route learning, Route risk assessments, Vehicle type training, Company induction' }
        ]
      }
    },
    {
      id: 'expertise',
      title: 'C9 Expertise & Experience',
      icon: Award,
      content: {
        overview: 'Partnering with C9 brings extensive experience in large-scale driver recruitment and mobilisation, with a proven track record.',
        achievements: [
          'Access to 35,000+ driver database with proven track record supplying PCV drivers for clients across the UK',
          'Partnering with local authorities, operators and consultancies',
          'Managing complex mobilisation projects involving multi-depot deployments',
          'Proven success in driver training and compliance',
          'Commitment to the wellbeing of our employees',
          'Improved transparency and communication'
        ],
        experience: 'Alfie Rae (Chief Executive) & Mustafa Ali (Client Success Manager) have direct, front-line experience of working on the Bee Network mobilisation, and both were based in Manchester throughout the pre-launch, launch and post-launch periods.',
        innovation: 'Creative problem solving and innovation, attention to detail, along with big picture thinking ensure this mobilisation strategy is both deliverable and successful.'
      }
    },
    {
      id: 'management',
      title: 'Account Management & Support Structure',
      icon: UserCheck,
      content: {
        overview: 'To ensure smooth delivery, we will provide a dedicated mobilisation team, working in partnership with LCRCA and/or Operators.',
        structure: 'Dedicated mobilisation team assigned with organisational chart to be provided (AR to insert chart)',
        approach: 'Partnership-focused approach ensuring clear communication protocols and seamless collaboration throughout the mobilisation process.'
      }
    },
    {
      id: 'assessment',
      title: 'Assessment & Training Process',
      icon: ClipboardCheck,
      content: {
        overview: 'Comprehensive staged assessment and training process ensuring all drivers meet operational and compliance requirements before deployment.',
        stage1: {
          title: 'Stage 1: Name Check & Document Verification',
          details: 'ID, right-to-work checks, licence verification, and initial screening'
        },
        stage2: {
          title: 'Stage 2: Driving & Medical Assessment',
          details: 'On-road driving assessment by qualified assessors, drug & alcohol testing if required'
        },
        induction: {
          title: 'Induction',
          details: 'Induction within one week of successful assessment, introduction to depot procedures, ticketing systems, company policies'
        },
        route_learning: {
          title: 'Route Learning',
          details: 'Route learning plan provided; recommend starting with limited set of 3–5 routes for familiarity'
        },
        mentoring: {
          title: 'Mentoring',
          details: 'Drivers paired with experienced mentors or route guides for hands-on route training'
        },
        signoff: {
          title: 'Final Sign-off',
          details: 'Final route competency and area knowledge confirmed by designated mentor or supervisor'
        }
      }
    },
    {
      id: 'attraction',
      title: 'Driver Attraction Strategy',
      icon: Megaphone,
      content: {
        overview: 'Multi-channel driver attraction campaign with relocation strategy playing a pivotal role in attracting drivers outside of commutable distance.',
        audiences: {
          primary: [
            'Local PCV Drivers working for other bus and coach operators',
            'Drivers displaced from depot closures or operator reductions due to end of seasonal work'
          ],
          secondary: [
            'Local PCV holders who are currently inactive, semi-retired or have certain availability',
            'Part-time drivers overlooked by Bee Network operators who were unwilling to consider flexible working',
            'Relocation drivers from outside the immediate region'
          ]
        },
        channels: [
          'Digital Advertising: Indeed, CV-Library, social media (Facebook/Instagram), targeted PPC ads',
          'Job Fairs & Roadshows: Local events across Merseyside and nearby counties (Warrington, Cheshire, Greater Manchester)',
          'Driver Referral Programme: Bonuses paid by C9 to existing drivers for successful referrals',
          'Industry Partnerships: Collaboration with PCV training schools, employment schemes, and logistics staffing groups'
        ]
      }
    },
    {
      id: 'relocation',
      title: 'Relocation Model',
      icon: Home,
      content: {
        overview: 'To widen the available talent pool and attract experienced drivers from outside the immediate region, we offer a structured relocation offering completely managed by C9 Recruitment.',
        model: 'Proven model highly utilised by the Bee Network due to local driver shortages. Accommodation paid for and managed by C9 Recruitment and invoiced back at cost.',
        accommodation: [
          'Fully serviced, high-quality accommodation sourced via professional property management company',
          'Properties typically range from 2 to 6 bedrooms, with each driver allocated a private bedroom',
          'Communal areas include shared kitchen, bathroom(s), and living space, maintained to consistent standard',
          'Flexible 28-day rolling contracts to accommodate changing operational needs',
          'Costs fully inclusive of utilities (electricity, water, gas, internet, council tax)',
          'Security deposits may be required, subject to individual property terms',
          'Strategically located for ease of access to depots or route start points where possible'
        ],
        identification: 'Relocation drivers identified at initial vetting stage for clear understanding before onboarding completion.'
      }
    },
    {
      id: 'proposition',
      title: 'Driver Proposition (Pay & Benefits)',
      icon: PoundSterling,
      content: {
        rates: {
          gnw: {
            paye: '£17.53 flat rate',
            umb: '£22.50 flat rate',
            relocation_paye: '£169 allowance',
            relocation_umb: '£198 allowance'
          },
          arriva: {
            weekday: '£17.63 per hour (Monday to Friday)',
            weekend: '£22.40 per hour (Saturday & Sunday)',
            bank_holiday: '£26.45 per hour',
            overtime: 'Flat rate'
          }
        },
        attractions: [
          'Guaranteed 50 Hours Per Week Over 5 Days',
          'Minimum 10-Hour Shifts (Maximum 1 hour break)',
          'Overtime Available on Rest Days',
          'Competitive with Bee Network rates'
        ]
      }
    },
    {
      id: 'training',
      title: 'Assessment & Training (OPTIONAL Ridgeway * Beverley Bell Partnership)',
      icon: GraduationCap,
      content: {
        partnership: 'Ridgeway Training selected as preferred training provider. Ridgeway Training was acquired by Beverley Bell Training & Consulting in July 2024, providing access to broader services.',
        leadership: 'Beverley Bell\'s leadership as former Senior Traffic Commissioner brings specialist industry knowledge and influence within the road transport sector.',
        expertise: 'Expert advice and guidance on all aspects of safety, compliance, best practice, training, and cost-effective operations across all client business layers.',
        availability: 'This partnership is offered as an optional service enhancement, with both Ridgeway Training and Beverley Bell Consulting & Training available to provide comprehensive training solutions.',
        courses: [
          'OLAT (Operator Licence Awareness Training)',
          'TM CPC (Transport Manager Certificate of Professional Competence)',
          'First Aid training',
          'Mental Health awareness',
          'Driver CPC modules',
          'E-learning driver platform (in development)'
        ]
      }
    },
    {
      id: 'supply',
      title: 'Supply Model',
      icon: Clock,
      content: {
        commitment: 'Minimum 12-week commitment expected from temporary drivers, with potential for extension based on ongoing needs.',
        timeline: 'Assessments, training, and onboarding will take approximately 3 to 6 weeks, depending on specific training requirements.',
        mobilisation: 'Remaining period begins with mobilisation, drivers required consistently for first 1 to 4 weeks while TUPE drivers complete transfer and training.',
        standby: 'In the event that full cohort of drivers transfers across, our drivers remain on standby for remainder of 12-week period.',
        flexibility: 'Drivers continue to be paid guaranteed hours and remain available to cover sickness or holiday absences.'
      }
    },
    {
      id: 'onsite',
      title: 'Onsite Management',
      icon: Settings,
      content: {
        presence: 'Dedicated C9 operations presence at depot(s) to ensure seamless service delivery.',
        reporting: 'Live reporting and performance dashboard providing real-time insights.',
        monitoring: 'Continuous performance monitoring and support structure.',
        support: 'Real-time support structure ensuring immediate response to operational needs.'
      }
    },
    {
      id: 'technology',
      title: 'AI & Technology Integration',
      icon: Brain,
      content: {
        systems: [
          'Automated DVLA checks for drivers completed via direct DVLA API',
          'Applicant Tracking System providing dashboard for LCRCA/Operator with live driver data and operational history',
          'Information on candidates being processed through onboarding and current stage tracking',
          'Driver ID Cards with scannable QR codes linking to driver profiles on ATS',
          'Route knowledge and relevant information accessible via QR system',
          'Integration capabilities with existing DAS systems'
        ],
        customisation: 'ATS configured in-house and can be tailored to meet specific data requirements.',
        future: 'Future development includes route integration and depot analytics dashboard capabilities.'
      }
    },
    {
      id: 'compliance',
      title: 'Compliance Framework',
      icon: Shield,
      content: {
        principle: '"Powered by People, Driven by Compliance" - our guiding principle reflecting strong commitment to regulatory standards.',
        contribution: 'Frequent contributions to RouteOne in collaboration with industry solicitors, raising awareness of compliance issues often overlooked by operators employing temporary staff.',
        payment: 'All drivers paid via traditional PAYE or via partnered FCSA Umbrella Company – Parasol.',
        standards: 'Comprehensive compliance framework ensuring all regulatory requirements are met and maintained throughout the engagement period.'
      }
    }
  ]

  // Search functionality
  useEffect(() => {
    if (searchTerm) {
      let count = 0
      sections.forEach(section => {
        const sectionText = JSON.stringify(section.content).toLowerCase()
        if (sectionText.includes(searchTerm.toLowerCase())) {
          count++
        }
      })
      setSearchResults(count)
    } else {
      setSearchResults(0)
    }
  }, [searchTerm])

  // Category mapping for sections
  const getSectionCategory = (sectionId) => {
    if (['position', 'objective'].includes(sectionId)) return 'strategy'
    if (['attraction', 'relocation', 'management', 'assessment', 'mobilisation'].includes(sectionId)) return 'operations'
    if (['proposition', 'training', 'supply'].includes(sectionId)) return 'execution'
    if (['technology', 'compliance'].includes(sectionId)) return 'support'
    if (sectionId === 'expertise') return 'expertise'
    return 'strategy'
  }

  // Smart filtering with both search and category
  const getFilteredSections = () => {
    let filtered = sections

    // Filter by category first
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(section => {
        const category = getSectionCategory(section.id)
        return category === selectedCategory || 
               (selectedCategory === 'strategy' && category === 'expertise') // Include expertise with strategy
      })
    }

    // Then filter by search term
    if (searchTerm) {
      filtered = filtered.filter(section => {
        const searchLower = searchTerm.toLowerCase()
        
        // Search in title
        if (section.title.toLowerCase().includes(searchLower)) return true
        
        // Search in all content fields
        const searchInContent = (obj) => {
          if (typeof obj === 'string') {
            return obj.toLowerCase().includes(searchLower)
          }
          if (Array.isArray(obj)) {
            return obj.some(item => 
              typeof item === 'string' 
                ? item.toLowerCase().includes(searchLower)
                : typeof item === 'object' && searchInContent(item)
            )
          }
          if (typeof obj === 'object' && obj !== null) {
            return Object.values(obj).some(value => searchInContent(value))
          }
          return false
        }
        
        return searchInContent(section.content)
      })
    }

    return filtered
  }

  const filteredSections = getFilteredSections()

  // Update search results count
  useEffect(() => {
    setSearchResults(filteredSections.length)
  }, [searchTerm, selectedCategory, filteredSections.length])

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-c9-blue via-c9-blue-alt to-slate-800 flex items-center justify-center p-4">
        <SEOHead 
          title="LCRCA Bus Franchising Proposal - Access Required"
          description="Secure access to C9 Recruitment's LCRCA bus franchising proposal"
          noIndex={true}
        />
        
        {/* Background patterns */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,110,0,0.1) 35px, rgba(255,110,0,0.1) 70px)`
          }}></div>
          <div className="absolute top-20 left-20 w-96 h-96 border-2 border-orange-300 rounded-full opacity-20"></div>
          <div className="absolute bottom-20 right-20 w-64 h-64 border-2 border-orange-300 rounded-full opacity-20"></div>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative z-10">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-c9-orange rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-c9-blue mb-2">LCRCA Bus Franchising Proposal</h1>
            <p className="text-gray-600">Contingency Plan for Driver Mobilisation</p>
            <p className="text-sm text-gray-500 mt-2">Prepared by C9 Recruitment for Grant Thornton</p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            <div className="relative">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Access Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-c9-orange focus:border-transparent transition-all"
                  placeholder="Enter password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-c9-orange hover:bg-c9-orange-alt text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              Access Proposal
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>For access, contact:</p>
            <p className="font-medium text-c9-blue">sam.wharfe@c9-group.co.uk</p>
            <p className="font-medium text-c9-blue">alfie.rae@c9-recruitment.com</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title="LCRCA Bus Franchising Proposal - C9 Recruitment"
        description="Comprehensive contingency plan for driver mobilisation in Liverpool City Region bus franchising"
        noIndex={true}
      />

      {/* Background patterns */}
      <div className="fixed inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,110,0,0.1) 35px, rgba(255,110,0,0.1) 70px)`
        }}></div>
      </div>

      {/* Header */}
      <header className="bg-gradient-to-r from-c9-blue to-c9-blue-alt text-white shadow-xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,0.1) 35px, rgba(255,255,255,0.1) 70px)`
          }}></div>
        </div>
        
        <div className="container-full py-10 relative z-10">
          <div className="text-center">
            {/* Partner Logos */}
            <div className="mb-8 mt-8">
              <div className="flex items-center justify-center gap-6 md:gap-8 mb-6">
                {/* C9 Recruitment Logo */}
                <img 
                  src={c9LogoImage} 
                  alt="C9 Recruitment Logo" 
                  className="h-12 md:h-14 w-auto object-contain drop-shadow-lg brightness-110"
                />
                
                {/* Partnership Indicator */}
                <div className="flex items-center gap-2 text-white/80">
                  <div className="w-6 h-0.5 bg-c9-orange"></div>
                  <span className="text-xs font-medium tracking-wide">FOR</span>
                  <div className="w-6 h-0.5 bg-c9-orange"></div>
                </div>
                
                {/* LCRCA Logo */}
                <img 
                  src={lcrcaLogoImage} 
                  alt="Liverpool City Region Combined Authority Logo" 
                  className="h-12 md:h-14 w-auto object-contain drop-shadow-lg"
                  style={{
                    filter: 'brightness(2.2) contrast(1.6) saturate(1.0)'
                  }}
                />
              </div>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-orange-300 mb-4">
              Contingency Plan for Driver Mobilisation
            </h1>
            <div className="space-y-2 mb-8">
              <div className="text-sm text-white/90">
                Prepared by C9 Recruitment for Grant Thornton / LCRCA
              </div>
              <div className="text-sm text-orange-300">
                Project Start: September 2026
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Search Bar */}
      <div className="bg-white/90 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="container-full py-8">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search across all sections... Try 'drivers', 'training', 'compliance', 'technology'"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-c9-orange focus:border-transparent transition-all text-base bg-white shadow-sm hover:shadow-md"
            />
            {searchTerm && (
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                {searchResults > 0 ? (
                  <div className="text-sm text-c9-orange font-medium bg-orange-50 px-3 py-1 rounded-full">
                    {searchResults} match{searchResults !== 1 ? 'es' : ''} found
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-full">
                    No matches found
                  </div>
                )}
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  title="Clear search"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
          
          {/* Filter Results Summary */}
          {(searchTerm || selectedCategory !== 'all') && (
            <div className="mt-4 text-center">
              <div className="text-sm text-gray-600">
                {searchResults > 0 ? (
                  <>
                    Showing <span className="font-semibold text-c9-orange">{searchResults}</span> section{searchResults !== 1 ? 's' : ''}
                    {searchTerm && (
                      <> matching <span className="font-semibold">"{searchTerm}"</span></>
                    )}
                    {selectedCategory !== 'all' && (
                      <> in <span className="font-semibold capitalize">{selectedCategory}</span> category</>
                    )}
                  </>
                ) : (
                  <>
                    {searchTerm ? (
                      <>
                        No sections found for <span className="font-semibold">"{searchTerm}"</span>
                        {selectedCategory !== 'all' && (
                          <> in <span className="font-semibold capitalize">{selectedCategory}</span> category</>
                        )}. 
                        Try searching for keywords like "drivers", "training", "compliance", or "technology".
                      </>
                    ) : (
                      <>No sections found in <span className="font-semibold capitalize">{selectedCategory}</span> category.</>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-16 z-30 shadow-sm">
        <div className="container-full">
          {/* Dashboard Navigation Categories */}
          <div className="flex justify-center py-4">
                          <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setSelectedCategory('all')
                    setSearchTerm('')
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 whitespace-nowrap text-sm font-medium ${
                    selectedCategory === 'all' 
                      ? 'bg-c9-orange text-white' 
                      : 'text-c9-orange bg-orange-50 hover:bg-orange-100'
                  }`}
                >
                  <Target className="w-4 h-4" />
                  <span>Overview</span>
                </button>

                <button
                  onClick={() => {
                    setSelectedCategory('strategy')
                    setSearchTerm('')
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 whitespace-nowrap text-sm font-medium ${
                    selectedCategory === 'strategy' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-blue-700 bg-blue-100 hover:bg-blue-200'
                  }`}
                >
                  <MapPin className="w-4 h-4" />
                  <span>Strategy</span>
                </button>

                <button
                  onClick={() => {
                    setSelectedCategory('operations')
                    setSearchTerm('')
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 whitespace-nowrap text-sm font-medium ${
                    selectedCategory === 'operations' 
                      ? 'bg-purple-600 text-white' 
                      : 'text-purple-700 bg-purple-100 hover:bg-purple-200'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  <span>Operations</span>
                </button>

                <button
                  onClick={() => {
                    setSelectedCategory('execution')
                    setSearchTerm('')
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 whitespace-nowrap text-sm font-medium ${
                    selectedCategory === 'execution' 
                      ? 'bg-green-600 text-white' 
                      : 'text-green-700 bg-green-100 hover:bg-green-200'
                  }`}
                >
                  <GraduationCap className="w-4 h-4" />
                  <span>Execution</span>
                </button>

                <button
                  onClick={() => {
                    setSelectedCategory('support')
                    setSearchTerm('')
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 whitespace-nowrap text-sm font-medium ${
                    selectedCategory === 'support' 
                      ? 'bg-gray-600 text-white' 
                      : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  <Settings className="w-4 h-4" />
                  <span>Support</span>
                </button>
              </div>
             </div>
           </div>
       </nav>

      {/* Main Content */}
      <main className="py-3 px-1 md:px-2 lg:px-3">
        <div className="max-w-none mx-auto space-y-1">
          
          {/* Hero Dashboard Overview */}
          <div className="hero-section grid grid-cols-1 md:grid-cols-2 gap-1 mb-2">
            {/* Main Hero Card */}
            <div className="bg-gradient-to-br from-c9-blue via-c9-blue to-c9-blue-alt rounded-2xl p-8 text-white relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="w-8 h-8 text-c9-orange" />
                  <span className="text-sm font-medium text-orange-300 uppercase tracking-wide">Mission Critical</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-black mb-4 leading-tight">
                  Driver Mobilisation for LCRCA Franchising
                </h2>
                <p className="text-lg text-white/90 mb-6 leading-relaxed">
                  Ensuring seamless transition with a fully trained, compliant, and route-ready workforce 
                  across both depots for September 2026 launch.
                </p>
                
                {/* Key Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-2xl font-black text-c9-orange">300+</div>
                    <div className="text-sm text-white/80">Drivers Required</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-2xl font-black text-c9-orange">2</div>
                    <div className="text-sm text-white/80">Depot Locations</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 md:col-span-1 col-span-2">
                    <div className="text-2xl font-black text-c9-orange">12wk</div>
                    <div className="text-sm text-white/80">Minimum Engagement</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Metrics Card */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="text-xl font-bold text-c9-blue mb-6">Project Overview</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Start Date</span>
                  <span className="font-semibold text-c9-blue">Sep 2026</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Locations</span>
                  <span className="font-semibold text-c9-blue">Wirral & St Helens</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">C9 Experience</span>
                  <span className="font-semibold text-c9-orange">35,000+ Driver Database</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Training Partner (Optional)</span>
                  <div className="text-right">
                    <div className="font-semibold text-c9-blue text-sm">Ridgeway Training</div>
                    <div className="font-medium text-c9-blue text-xs">Beverley Bell Consulting & Training</div>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    Ready for Implementation
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dynamic Content Based on Search and Category Filter */}
          {(searchTerm || selectedCategory !== 'all') ? (
            /* Search Results */
            filteredSections.length > 0 ? (
                          <div className="space-y-2">
              <div className="text-center mb-3">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">
                    {searchTerm ? 'Search Results' : `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Sections`}
                  </h2>
                  <p className="text-gray-600">
                    {searchTerm ? (
                      <>Found {filteredSections.length} section{filteredSections.length !== 1 ? 's' : ''} matching "{searchTerm}"</>
                    ) : (
                      <>Showing {filteredSections.length} section{filteredSections.length !== 1 ? 's' : ''} in {selectedCategory} category</>
                    )}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {filteredSections.map((section) => {
                    // Determine theme based on section type
                    let theme = 'strategy'
                    if (['attraction', 'relocation', 'management', 'mobilisation'].includes(section.id)) theme = 'operations'
                    if (['proposition', 'training', 'supply'].includes(section.id)) theme = 'execution'
                    if (['technology', 'compliance'].includes(section.id)) theme = 'support'
                    if (section.id === 'expertise') theme = 'expertise'
                    
                    return (
                      <DashboardCard
                        key={section.id}
                        section={section}
                        theme={theme}
                        size="medium"
                        searchTerm={searchTerm}
                      />
                    )
                  })}
                </div>
              </div>
            ) : (
              /* No Results */
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No Results Found</h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm ? (
                    <>We couldn't find any sections matching "<span className="font-semibold">{searchTerm}</span>"</>
                  ) : (
                    <>No sections available in the <span className="font-semibold">{selectedCategory}</span> category</>
                  )}
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <p>Try searching for:</p>
                  <div className="flex flex-wrap justify-center gap-2 mt-3">
                    {['drivers', 'training', 'compliance', 'technology', 'recruitment', 'mobilisation', 'LCRCA'].map((suggestion) => (
                      <button
                        key={suggestion}
                        onClick={() => setSearchTerm(suggestion)}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700 transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )
          ) : (
            /* Default Dashboard Layout */
                          <>
                {/* Strategy Section */}
                <div className="mt-48 mb-8">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-black text-c9-blue mb-3 tracking-wide">STRATEGY</h2>
                    <div className="w-24 h-1 bg-gradient-to-r from-c9-orange to-c9-blue mx-auto rounded-full"></div>
                  </div>
                  <div className="strategy-section grid grid-cols-1 md:grid-cols-2 gap-1">
                                      <DashboardCard section={sections.find(s => s.id === 'objective')} theme="strategy" size="large" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'position')} theme="strategy" size="large" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'expertise')} theme="strategy" size="medium" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'mobilisation')} theme="strategy" size="medium" searchTerm={searchTerm} />
                </div>
                </div>

                {/* Operations Section */}
                <div className="mt-48 mb-8">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-black text-c9-blue mb-3 tracking-wide">OPERATIONS</h2>
                    <div className="w-24 h-1 bg-gradient-to-r from-c9-orange to-c9-blue mx-auto rounded-full"></div>
                  </div>
                  <div className="operations-section grid grid-cols-1 md:grid-cols-2 gap-1">
                    <DashboardCard section={sections.find(s => s.id === 'attraction')} theme="operations" size="large" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'relocation')} theme="operations" size="large" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'management')} theme="operations" size="large" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'assessment')} theme="operations" size="large" searchTerm={searchTerm} />
                  </div>
                </div>

                {/* Execution Section */}
                <div className="mt-48 mb-8">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-black text-c9-blue mb-3 tracking-wide">EXECUTION</h2>
                    <div className="w-24 h-1 bg-gradient-to-r from-c9-orange to-c9-blue mx-auto rounded-full"></div>
                  </div>
                  <div className="execution-section grid grid-cols-1 md:grid-cols-2 gap-1">
                    <DashboardCard section={sections.find(s => s.id === 'proposition')} theme="execution" size="medium" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'training')} theme="execution" size="medium" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'supply')} theme="execution" size="medium" searchTerm={searchTerm} />
                  </div>
                </div>

                {/* Support Section */}
                <div className="mt-48 mb-8">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-black text-c9-blue mb-3 tracking-wide">SUPPORT</h2>
                    <div className="w-24 h-1 bg-gradient-to-r from-c9-orange to-c9-blue mx-auto rounded-full"></div>
                  </div>
                  <div className="support-section grid grid-cols-1 md:grid-cols-2 gap-1">
                    <DashboardCard section={sections.find(s => s.id === 'technology')} theme="support" size="medium" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'compliance')} theme="support" size="medium" searchTerm={searchTerm} />
                  </div>
                  
                  {/* Centered Compliance Ready Section */}
                  <div className="mt-16 flex justify-center">
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 border border-green-200 max-w-md w-full">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Shield className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="font-bold text-green-800 mb-3 text-lg">Compliance Ready</h3>
                        <p className="text-green-700 mb-2">"Powered by People, Driven by Compliance"</p>
                        <div className="text-sm text-green-600">PAYE & FCSA Umbrella Options Available</div>
                      </div>
                    </div>
                  </div>
                </div>
            </>
          )}

        </div>


      </main>

      {/* Footer */}
      <footer className="bg-c9-blue text-white py-8 mt-16">
        <div className="container-full">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* C9 Branding */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-c9-orange rounded-lg flex items-center justify-center">
                <span className="text-white font-black text-xl">C9</span>
              </div>
              <div>
                <h3 className="text-xl font-bold">C9 Recruitment</h3>
                <p className="text-orange-300">Powered by People, Driven by Compliance</p>
              </div>
            </div>
            
            {/* LCRCA Logo */}
            <div className="flex items-center gap-4">
              <img 
                src={lcrcaLogoImage} 
                alt="Liverpool City Region Combined Authority Logo" 
                className="h-10 w-auto object-contain opacity-80"
              />
            </div>
          </div>
          
          <div className="border-t border-white/20 mt-6 pt-6 text-center">
            <p className="text-gray-300 mb-4">
              This document is confidential and proprietary to C9 Recruitment.
            </p>
            <div className="text-sm text-gray-400">
              <p>For questions or support:</p>
              <p>Sam Wharfe: sam.wharfe@c9-group.co.uk | Alfie Rae: alfie.rae@c9-recruitment.com</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

// Dashboard Card Component
function DashboardCard({ section, theme, size, searchTerm }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const Icon = section.icon

  const highlightText = (text) => {
    if (!searchTerm || !text) return text
    
    try {
      // Escape special regex characters but preserve the search term's meaning
      const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`(${escapedSearchTerm})`, 'gi')
      return text.replace(regex, '<mark class="bg-yellow-300 text-yellow-900 px-1 py-0.5 rounded font-medium">$1</mark>')
    } catch (error) {
      // If regex fails, return original text
      return text
    }
  }

  const getThemeClasses = () => {
    switch (theme) {
      case 'strategy':
        return 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:from-blue-100 hover:to-blue-150'
      case 'expertise':
        return 'bg-gradient-to-br from-c9-orange/10 to-c9-orange/20 border-orange-200 hover:from-c9-orange/20 hover:to-c9-orange/30'
      case 'operations':
        return 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:from-purple-100 hover:to-purple-150'
      case 'execution':
        return 'bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:from-green-100 hover:to-green-150'
      case 'support':
        return 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200 hover:from-gray-100 hover:to-gray-150'
      default:
        return 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'
    }
  }

  const getIconColor = () => {
    switch (theme) {
      case 'strategy': return 'text-blue-600'
      case 'expertise': return 'text-c9-orange'
      case 'operations': return 'text-purple-600'
      case 'execution': return 'text-green-600'
      case 'support': return 'text-gray-600'
      default: return 'text-c9-blue'
    }
  }

  const getSizeClasses = () => {
    return 'min-h-[300px]'
  }

  const getButtonClasses = () => {
    switch (theme) {
      case 'strategy':
        return 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
      case 'expertise':
        return 'bg-c9-orange text-white hover:bg-orange-600 hover:shadow-lg'
      case 'operations':
        return 'bg-purple-600 text-white hover:bg-purple-700 hover:shadow-lg'
      case 'execution':
        return 'bg-green-600 text-white hover:bg-green-700 hover:shadow-lg'
      case 'support':
        return 'bg-gray-600 text-white hover:bg-gray-700 hover:shadow-lg'
      default:
        return 'bg-c9-blue text-white hover:bg-blue-700 hover:shadow-lg'
    }
  }

  const getCardSummary = () => {
    switch (section.id) {
      case 'objective':
        return 'Ensure the timely mobilisation of a fully trained, compliant, and route-ready driver workforce across one or both depots for the Liverpool City Region franchising commencing September 2026. This includes delivering a workforce that meets all regulatory and operational requirements (PCV, CPC, Medical & Training), is flexible and scalable to adapt to award outcomes, and is supported by comprehensive contingency plans to mitigate risks to service continuity. The overarching aim is to provide a resilient, low-risk mobilisation solution that supports a smooth transition under the franchising model while maintaining high service standards from day one.'
      case 'position':
        return 'The first tranche of the Liverpool City Region Combined Authority (LCRCA) franchising rollout will consist of two operational lots: Wirral and St Helens. Both areas are currently served by Arriva, who under the new franchising model will only be eligible to win a maximum of one of these lots. This introduces significant strategic risks for LCRCA and the new operators, including potential service disruption, missing buses, increased waiting times due to staff shortages, and negative media attention. Arriva may be reluctant to share driver data or could freeze recruitment efforts, creating potential resourcing gaps for incoming operators.'
      case 'expertise':
        return 'Partnering with C9 brings extensive experience in large-scale driver recruitment and mobilisation, with access to a 35,000+ driver database and a proven track record of success. We have supplied PCV drivers for clients across the UK, partnered with local authorities and operators, managed complex multi-depot mobilisation projects, and demonstrated proven success in driver training and compliance. Our Chief Executive Alfie Rae and Client Success Manager Mustafa Ali have direct, front-line experience working on the Bee Network mobilisation, providing invaluable insights and lessons learned to ensure this mobilisation strategy is both deliverable and successful.'
      case 'mobilisation':
        return 'Comprehensive strategic planning and implementation approach covering an estimated 300+ drivers required across 2 depots (Wirral & St Helens) for the September 2026 franchising start date. This includes managing full-time, part-time, and weekend-only working patterns while ensuring all compliance requirements are met: valid PCV licence, CPC certification, medical clearance, route learning, route risk assessments, vehicle type training, and company induction. The mobilisation plan addresses the complex operational challenges of transitioning from the current operator structure to the new franchising model while maintaining service continuity and operational excellence.'
      case 'attraction':
        return 'Multi-channel driver attraction campaign designed to meet the scale and timeline of the Liverpool City Region franchising mobilisation. Our strategy targets priority audiences including local PCV drivers working for other operators, drivers displaced from depot closures, local inactive PCV holders including semi-retired drivers, and national relocation candidates. We utilize digital advertising (Indeed, CV-Library, social media), job fairs and roadshows across Merseyside and nearby counties, comprehensive driver referral programmes with bonuses, and strategic industry partnerships with PCV training schools and employment schemes to ensure maximum reach and effectiveness.'
      case 'relocation':
        return 'Fully managed relocation offering designed to widen the available talent pool and attract experienced drivers from outside the immediate region. This proven model, highly utilized by the Bee Network due to local driver shortages, includes fully serviced, high-quality accommodation sourced via professional property management companies. Properties typically range from 2 to 6 bedrooms with private rooms for each driver, communal facilities maintained to consistent standards, flexible 28-day rolling contracts, all-inclusive utilities (electricity, water, gas, internet, council tax), QR-coded tracking and vetting for each driver, and strategic location placement for optimal depot access.'
      case 'management':
        return 'Dedicated mobilisation team providing comprehensive account management and support structure to ensure smooth delivery in partnership with LCRCA and/or the awarded operators. Our on-site management approach includes dedicated operational presence at depot locations, live reporting and performance dashboards for real-time visibility, 24/7 support throughout the mobilisation period, partnership-focused collaboration with all stakeholders, and continuous performance monitoring. This hands-on approach ensures seamless communication, rapid issue resolution, and maintains the high standards required for successful franchise operations from day one.'
      case 'assessment':
        return 'Comprehensive staged assessment and training process ensuring all drivers meet operational and compliance requirements before deployment. Stage 1 includes name check and document verification with ID, right-to-work checks, and licence verification. Stage 2 involves on-road driving assessment by qualified assessors with drug & alcohol testing if required. This is followed by induction within one week covering depot procedures, ticketing systems, and company policies, route learning plans starting with 3-5 routes for familiarity, mentoring with experienced route guides for hands-on training, and final route competency sign-off by designated supervisors to ensure operational readiness.'
      case 'proposition':
        return 'Competitive and attractive driver proposition designed to attract and retain quality drivers throughout the mobilisation period. Our pay structure includes £17.53 PAYE flat rate and £22.50 umbrella rate, with relocation allowances of £169 PAYE / £198 umbrella. Key attractions include guaranteed 50 hours per week over 5 days, minimum 10-hour shifts with maximum 1-hour breaks, overtime opportunities available on rest days, and rates benchmarked competitively against the Bee Network and current Arriva Merseyside rates. This comprehensive package ensures we can attract experienced drivers while providing the financial incentives necessary for successful mobilisation.'
      case 'training':
        return 'Optional training partnership enhancement with Ridgeway Training and Beverley Bell Consulting & Training. Ridgeway Training was acquired by Beverley Bell Training & Consulting in July 2024, providing access to broader specialized services. Beverley Bell\'s leadership as former Senior Traffic Commissioner brings specialist industry knowledge and influence within the road transport sector. This optional partnership offers expert advice and guidance on all aspects of safety, compliance, best practice, training, and cost-effective operations across all client business layers, with comprehensive course offerings including OLAT, TM CPC, First Aid, Mental Health awareness, Driver CPC modules, and e-learning platforms in development.'
      case 'supply':
        return 'Structured supply model with 12-week minimum driver commitment, ensuring operational stability and continuity throughout the transition period. Our approach includes 3-6 week assessment, training, and onboarding phases, followed by active mobilisation support during the first 1-4 weeks while TUPE drivers complete their transfer and necessary training. In scenarios where full driver cohorts transfer across, our drivers remain on standby for the remainder of the 12-week period, continuing to receive guaranteed hours while remaining available to cover sickness, holiday absences, or any operational gaps. This provides operators with maximum flexibility and risk mitigation during the critical transition phase.'
      case 'technology':
        return 'Advanced technology integration providing comprehensive workforce management and compliance monitoring capabilities. Our system includes direct DVLA API integration for automated driver licence checking, custom Applicant Tracking System (ATS) providing live dashboards for LCRCA/operators showing driver status and operational history, QR-coded driver ID cards with scannable links to complete driver profiles including route knowledge and qualification details, and future development plans for route integration and depot analytics dashboards. This technology infrastructure ensures full transparency, real-time monitoring, and seamless integration with existing operational systems.'
      case 'compliance':
        return 'Our guiding principle "Powered by People, Driven by Compliance" reflects our unwavering commitment to regulatory standards and best practices. We frequently contribute to RouteOne in collaboration with industry solicitors, raising awareness of compliance issues often overlooked by operators employing temporary staff. All drivers are paid via traditional PAYE or through our partnered FCSA Umbrella Company (Parasol), ensuring full regulatory compliance. Our comprehensive approach includes in-house auditing processes, legal commentary and support through RouteOne partnerships, and proactive compliance monitoring to ensure all regulatory requirements are consistently met and maintained throughout the engagement period.'
      default:
        return section.content.overview || section.content.details?.[0] || 'Comprehensive strategic planning and implementation approach covering all aspects of workforce mobilisation, operational readiness, and service delivery excellence.'
    }
  }

  return (
    <div className={`${getSizeClasses()} ${getThemeClasses()} rounded-lg border p-3 transition-all duration-300 hover:shadow-lg group h-full flex flex-col w-full`}>
      
      {/* Card Header */}
      <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
            <div className={`w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow`}>
              <Icon className={`w-5 h-5 ${getIconColor()}`} />
          </div>
          <div>
            <h3 
              className="font-bold text-gray-800 text-base leading-tight group-hover:text-gray-900 transition-colors"
              dangerouslySetInnerHTML={{ __html: highlightText(section.title) }}
            />
            <div className="text-xs text-gray-600 mt-0.5">
              {theme === 'strategy' && 'Strategic Planning'}
              {theme === 'expertise' && 'Core Capabilities'}
              {theme === 'operations' && 'Operational Excellence'}
              {theme === 'execution' && 'Implementation'}
              {theme === 'support' && 'Support Systems'}
            </div>
          </div>
        </div>

      </div>

      {/* Simplified Preview Content */}
      <div className="flex flex-col h-full">
        {/* Brief Summary */}
        <div className="flex-grow mb-4">
          <div 
            className="text-sm text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ 
              __html: highlightText(getCardSummary())
            }}
          />
        </div>
        
        {/* Large Show Detail Button - Always at bottom */}
        <div className="mt-auto">
          {!isExpanded ? (
            <button 
              className={`w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                getButtonClasses()
              }`}
              onClick={(e) => {
                e.stopPropagation()
                setIsExpanded(!isExpanded)
              }}
            >
              <span>SHOW DETAILS</span>
              <ChevronDown className="w-4 h-4" />
            </button>
          ) : (
            <button 
              className="w-full py-2 px-4 rounded-lg font-medium text-sm bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-200 flex items-center justify-center gap-2"
              onClick={(e) => {
                e.stopPropagation()
                setIsExpanded(!isExpanded)
              }}
            >
              <ChevronUp className="w-4 h-4" />
              <span>COLLAPSE</span>
            </button>
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-6 pt-6 border-t border-white/50 animate-fade-in">
          <SectionContent section={section} highlightText={highlightText} />
        </div>
      )}
    </div>
  )
}

// Section Content Component
function SectionContent({ section, highlightText }) {
  const { content } = section

  switch (section.id) {
    case 'objective':
      return (
        <div className="space-y-3">
          <div className="prose max-w-none">
            <p className="text-base text-neutral-800 leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(content.overview) }}></p>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="bg-orange-50 p-3 rounded-lg border border-orange-100">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">This workforce will:</h4>
              <ul className="space-y-1.5">
                {content.details.map((detail, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-neutral-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(detail) }}></span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
              <h4 className="font-semibold text-c9-blue mb-2 text-sm">Overall Goal:</h4>
              <p className="text-neutral-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(content.summary) }}></p>
            </div>
          </div>
        </div>
      )

    case 'position':
      return (
        <div className="space-y-4">
          <p className="text-base text-neutral-800 leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(content.overview) }}></p>
          <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded-r-lg border border-red-100">
            <h4 className="font-semibold text-red-800 mb-2 text-sm">Strategic Risks Identified:</h4>
            <ul className="space-y-1.5">
              {content.risks.map((risk, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-red-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(risk) }}></span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
            <h4 className="font-semibold text-c9-blue mb-2 text-sm">Risk Mitigation:</h4>
            <p className="text-neutral-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(content.mitigation) }}></p>
          </div>
        </div>
      )

    case 'mobilisation':
      return (
        <div className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            {content.requirements.map((req, idx) => (
              <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <dt className="font-semibold text-c9-blue text-sm" dangerouslySetInnerHTML={{ __html: highlightText(req.label) }}></dt>
                <dd className="text-neutral-700 mt-1 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightText(req.value) }}></dd>
              </div>
            ))}
          </div>
        </div>
      )

    case 'expertise':
      return (
        <div className="space-y-6">
          <p className="text-lg text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.overview) }}></p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-3">Key Achievements:</h4>
              <ul className="space-y-2">
                {content.achievements.map((achievement, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(achievement) }}></span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-c9-blue mb-3">Bee Network Experience:</h4>
                <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.experience) }}></p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-c9-blue mb-3">Our Approach:</h4>
                <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.innovation) }}></p>
              </div>
            </div>
          </div>
        </div>
      )

    case 'attraction':
      return (
        <div className="space-y-6">
          <p className="text-lg text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.overview) }}></p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-c9-blue mb-3">Primary Audiences:</h4>
                <ul className="space-y-2">
                  {content.audiences.primary.map((audience, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(audience) }}></span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-c9-blue mb-3">Secondary Audiences:</h4>
                <ul className="space-y-2">
                  {content.audiences.secondary.map((audience, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(audience) }}></span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-3">Attraction Channels:</h4>
              <ul className="space-y-2">
                {content.channels.map((channel, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(channel) }}></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )

    case 'relocation':
      return (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-lg text-gray-700 mb-4" dangerouslySetInnerHTML={{ __html: highlightText(content.overview) }}></p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-c9-blue mb-3">Proven Model:</h4>
                <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.model) }}></p>
              </div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-3">Accommodation Features:</h4>
              <ul className="space-y-2">
                {content.accommodation.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-c9-orange rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(feature) }}></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-3">Driver Identification:</h4>
            <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.identification) }}></p>
          </div>
        </div>
      )

    case 'assessment':
      return (
        <div className="space-y-6">
          <p className="text-lg text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.overview) }}></p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                <h4 className="font-semibold text-purple-800 mb-3">{content.stage1.title}</h4>
                <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.stage1.details) }}></p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                <h4 className="font-semibold text-purple-800 mb-3">{content.stage2.title}</h4>
                <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.stage2.details) }}></p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                <h4 className="font-semibold text-purple-800 mb-3">{content.induction.title}</h4>
                <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.induction.details) }}></p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h4 className="font-semibold text-blue-800 mb-3">{content.route_learning.title}</h4>
                <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.route_learning.details) }}></p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h4 className="font-semibold text-blue-800 mb-3">{content.mentoring.title}</h4>
                <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.mentoring.details) }}></p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                <h4 className="font-semibold text-green-800 mb-3">{content.signoff.title}</h4>
                <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.signoff.details) }}></p>
              </div>
            </div>
          </div>
        </div>
      )

    case 'proposition':
      return (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-3">GNW Agency Rates:</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>PAYE Flat Rate:</span>
                  <span className="font-semibold">{content.rates.gnw.paye}</span>
                </div>
                <div className="flex justify-between">
                  <span>Umbrella Flat Rate:</span>
                  <span className="font-semibold">{content.rates.gnw.umb}</span>
                </div>
                <div className="flex justify-between">
                  <span>Relocation (PAYE):</span>
                  <span className="font-semibold">{content.rates.gnw.relocation_paye}</span>
                </div>
                <div className="flex justify-between">
                  <span>Relocation (Umbrella):</span>
                  <span className="font-semibold">{content.rates.gnw.relocation_umb}</span>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-3">Arriva Merseyside Rates:</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Weekdays:</span>
                  <span className="font-semibold">{content.rates.arriva.weekday}</span>
                </div>
                <div className="flex justify-between">
                  <span>Weekends:</span>
                  <span className="font-semibold">{content.rates.arriva.weekend}</span>
                </div>
                <div className="flex justify-between">
                  <span>Bank Holidays:</span>
                  <span className="font-semibold">{content.rates.arriva.bank_holiday}</span>
                </div>
                <div className="flex justify-between">
                  <span>Overtime:</span>
                  <span className="font-semibold">{content.rates.arriva.overtime}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-3">Key Attractions for Agency Drivers:</h4>
            <ul className="space-y-2">
              {content.attractions.map((attraction, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(attraction) }}></span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )

    case 'training':
      return (
        <div className="space-y-6">
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-6">
            <h4 className="font-semibold text-amber-800 mb-3">Optional Service Enhancement</h4>
            <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.availability) }}></p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-semibold text-c9-blue mb-3">Partnership Structure:</h4>
                <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.partnership) }}></p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-c9-blue mb-3">Leadership Excellence:</h4>
                <p className="text-gray-700 mb-2" dangerouslySetInnerHTML={{ __html: highlightText(content.leadership) }}></p>
                <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.expertise) }}></p>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-c9-blue mb-3">Comprehensive Training Courses:</h4>
              <ul className="space-y-2">
                {content.courses.map((course, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(course) }}></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h4 className="font-semibold text-c9-blue mb-3">Note:</h4>
            <p className="text-gray-700">The detailed Assessment & Training Process is covered in the Operations section above. This section focuses on the optional training partnership and course offerings available to enhance service delivery.</p>
          </div>
        </div>
      )

    case 'supply':
      return (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-3">Commitment Period:</h4>
              <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.commitment) }}></p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-3">Timeline:</h4>
              <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.timeline) }}></p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-3">Mobilisation:</h4>
              <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.mobilisation) }}></p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-3">Flexibility:</h4>
              <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.flexibility) }}></p>
            </div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-3">Standby Arrangements:</h4>
            <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.standby) }}></p>
          </div>
        </div>
      )

    case 'onsite':
      return (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-orange-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-3">Operational Presence:</h4>
            <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.presence) }}></p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-3">Reporting:</h4>
            <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.reporting) }}></p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-3">Monitoring:</h4>
            <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.monitoring) }}></p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-3">Support Structure:</h4>
            <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.support) }}></p>
          </div>
        </div>
      )

    case 'technology':
      return (
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-3">Technology Systems:</h4>
            <ul className="space-y-2">
              {content.systems.map((system, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(system) }}></span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-3">Customisation:</h4>
              <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.customisation) }}></p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-3">Future Development:</h4>
              <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.future) }}></p>
            </div>
          </div>
        </div>
      )

    case 'compliance':
      return (
        <div className="space-y-4">
          <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-c9-orange">
            <h4 className="font-semibold text-c9-blue mb-3">Our Guiding Principle:</h4>
            <p className="text-lg font-medium text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.principle) }}></p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-3">Industry Contribution:</h4>
              <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.contribution) }}></p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-c9-blue mb-3">Payment Structure:</h4>
              <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.payment) }}></p>
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-c9-blue mb-3">Compliance Framework:</h4>
            <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: highlightText(content.standards) }}></p>
          </div>
        </div>
      )

    default:
      return (
        <div className="text-gray-600">
          Content for {section.title} is being prepared...
        </div>
      )
  }
} 